// Pre-present simulation tick for DungeonPlay (no framebuffer access).

import { hideGameHud } from '../ui/hud';
import type { World } from './world';
import type { InputFrame } from './input';
import { G, rooms, map } from './state';
import {
  FRAME_MS,
  PLAYERW,
  PLAYERH,
  SCREEN_CX,
  SCREEN_CY,
  TILE,
  S,
  K_UP,
  K_DN,
  K_LT,
  K_RT,
} from './constants';
import { Get, GetRoom, Put } from './mapgen';
import { SetTonedPalette } from '../render/palette';
import { rand as rnd } from './rng';
import {
  MoveEntities,
  ActivateRand,
  CreateGem,
  CircuitBullets,
  HurtEnemies,
  PlayerDist,
} from './demon';
import { BossControl, TryHurtBoss, CanGetArtifact, BossRoom } from './boss';
import { RecordRoom } from './gamemap';
import { LockDoors, ActivateRoom } from './room-actions';
import { keyHeld, applyInputFrameToKeyHeld } from './input';
import { SND_Pos } from '../audio/index';
import { tickDataEntities } from './entities-runtime';
import { processAbilityMovement } from './abilities-runtime';

const M_PI = Math.PI;
const tr = Math.trunc;

function sqr(x: number): number {
  return x * x;
}
function dist(x1: number, y1: number, x2: number, y2: number): number {
  return tr(Math.sqrt(sqr(x2 - x1) + sqr(y2 - y1)));
}
function min(x: number, y: number): number {
  return x < y ? x : y;
}

export type DungeonFrameCtx = {
  t: number;
  last_killed: number;
  n_arcs: number;
  arcdir: number;
  max_dist: number;
  agate_t: number;
};

export function createDungeonFrameCtx(max_dist: number): DungeonFrameCtx {
  return { t: 0, last_killed: 0, n_arcs: 0, arcdir: 0, max_dist, agate_t: 0 };
}

/** Simulation that ran before the render pass in the original loop. */
export function updateDungeonPreRender(world: World, ctx: DungeonFrameCtx): void {
  if (!G.game_paused) {
    if (G.player_dying > 30) {
      G.player_hp--;
      if (G.player_hp <= 0) {
        if (!G.training) G.player_lives--;
        let lost_gems = tr(G.player_gems / 3);
        G.player_gems -= lost_gems;
        lost_gems = tr((lost_gems * 95) / 100);
        while (lost_gems > 0) {
          const rg_x = rooms[G.player_room].x * TILE + TILE + (rnd() % (rooms[G.player_room].w * TILE - TILE * 2));
          const rg_y = rooms[G.player_room].y * TILE + TILE + (rnd() % (rooms[G.player_room].h * TILE - TILE * 2));
          const rg_v = rnd() % (tr(lost_gems / 4) + 2);
          CreateGem(rg_x, rg_y, G.player_room, rg_v);
          lost_gems -= rg_v;
        }
        G.player_dying = 0;
        G.shield_hp = 0;
        if (G.current_boss === 3 && G.boss_fight_mode !== 0) {
          G.player_x = G.enter_room_x;
          G.player_y = G.enter_room_y;
          G.prv_player_room = 1;
        } else {
          G.player_x = G.checkpoint_x;
          G.player_y = G.checkpoint_y;
        }
        G.scroll_home = 1;
        CircuitBullets(G.player_x, G.player_y, 100);
        G.player_hp = 3 + (G.player_shield === 30 ? 1 : 0) * 3;
      } else {
        G.player_dying = 0;
      }
    }
  }

  G.circuit_size = 250 + 50 * (G.circuit_fillrate + G.circuit_recoverrate);

  if (G.magic_circuit > 0) {
    G.circuit_range = tr((Math.sqrt(G.magic_circuit + 1) * 6 + min(tr(G.magic_circuit / 2), 50)) * 1.66);
    if (G.artifacts[3]) G.circuit_range += tr(G.circuit_range / 2.4);
  } else G.circuit_range = -1;

  G.player_room = GetRoom(tr(G.player_x / TILE), tr(G.player_y / TILE));

  if (G.player_room !== G.prv_player_room) {
    SetTonedPalette(rooms[G.player_room].s_dist / ctx.max_dist);
    G.prv_player_room = G.player_room;
    RecordRoom(G.player_room);
    G.enter_room_x = G.player_x;
    G.enter_room_y = G.player_y;
    if (rooms[G.player_room].room_type === 2) {
      LockDoors(G.player_room);
      BossRoom(G.player_room);
    }
    if ((rooms[G.player_room].checkpoint || G.player_room === 0) && !G.artifacts[11]) {
      G.checkpoint_x = rooms[G.player_room].x * TILE + tr(rooms[G.player_room].w / 2) * TILE + S(8);
      G.checkpoint_y = rooms[G.player_room].y * TILE + tr(rooms[G.player_room].h / 2) * TILE + S(4);
    }
    if (rooms[G.player_room].visited === 0) {
      rooms[G.player_room].visited = 1;
      G.explored++;
      if (G.explored === 3000) G.agate_knife_loc = G.player_room;
      ActivateRoom(G.player_room);
    }
  }

  if (ctx.last_killed !== G.killed_enemies) {
    SetTonedPalette(rooms[G.player_room].s_dist / ctx.max_dist);
    ctx.last_killed = G.killed_enemies;
  } else {
    if (G.player_room === 0 && G.artifacts[11] === 1) SetTonedPalette(0);
  }

  if (!G.map_enabled && !G.game_paused) {
    if (G.boss_fight_mode !== 0 && G.boss_fight_mode < 23) BossControl();
    MoveEntities();
    tickDataEntities(world, 1);
  }

  updateInlineWorldSim(ctx);
}

function updateInlineWorldSim(ctx: DungeonFrameCtx): void {
  if (G.map_enabled) return;

  if (G.player_dying !== 0 && !G.game_paused) G.player_dying++;

  if (G.player_room === G.agate_knife_loc) {
    const room_x = rooms[G.player_room].x * TILE + TILE;
    const room_y = rooms[G.player_room].y * TILE + TILE;
    const room_w = rooms[G.player_room].w * TILE - TILE * 2;
    const room_h = rooms[G.player_room].h * TILE - TILE * 2;
    const xpos = tr((Math.sin(ctx.agate_t * 1.33) * 0.5 + 0.5) * room_w) + room_x;
    const ypos = tr((Math.cos(ctx.agate_t * 0.7) * 0.5 + 0.5) * room_h) + room_y;
    if (dist(G.player_x, G.player_y, xpos, ypos) < S(20)) {
      G.agate_knife_loc = -1;
      G.specialmessage = 50;
      G.specialmessagetimer = 150;
      SND_Pos('dat/a/crystal2.wav', 128, 0);
      G.player_shield = 30;
      G.circuit_fillrate = 30;
      G.circuit_recoverrate = 30;
      G.player_hp = 6;
    }
    ctx.agate_t += 0.05;
  }

  if (G.opening_door_i > 0 && !G.game_paused) {
    G.opening_door_i++;
    if (G.opening_door_i >= 100) {
      G.opening_door_i = 0;
      Put(
        G.opening_door_x,
        G.opening_door_y,
        Get(G.opening_door_x, G.opening_door_y) - 38 + 13,
        GetRoom(G.opening_door_x, G.opening_door_y)
      );
    }
  }

  if (G.circuit_release > 0 && !G.game_paused) {
    CircuitBullets(G.release_x, G.release_y, tr((G.circuit_release * G.release_range) / 20));
    G.circuit_release += 2;
    if (G.circuit_release > 24) {
      G.circuit_release = 0;
      HurtEnemies(G.release_x, G.release_y, G.release_range, G.release_str);
      if (G.boss_fight_mode === 2) TryHurtBoss(G.release_x, G.release_y, G.release_range, G.release_str);
    }
  }

  if (!G.game_paused && G.shield_hp < G.player_shield) {
    const denom = 3 - G.training - (G.player_shield === 30 ? 1 : 0);
    G.shield_recover += tr((G.player_shield * 3) / denom);
    if (G.artifacts[1]) G.shield_recover += tr((G.player_shield * 3) / denom);
    if (G.shield_recover >= 50) {
      G.shield_hp++;
      G.shield_recover -= 50 - (G.player_shield === 30 ? 1 : 0) * 25;
    }
  }

  ctx.t++;
}

export type DungeonPostResult = {
  breakLoop: boolean;
  showEnding: boolean;
};

export async function updateDungeonPostPresent(
  world: World,
  input: InputFrame,
  ctx: DungeonFrameCtx,
  handleEvents: () => Promise<void>,
  releaseCircuit: () => void,
  touchTile: (ix: number, iy: number) => number
): Promise<DungeonPostResult> {

  let can_move = 1;
  if (G.player_dying !== 0 && G.player_hp <= 1) can_move = 0;
  if (rooms[G.player_room].room_type === 5)
    if (CanGetArtifact())
      if (Get(tr((G.player_x + PLAYERW / 2) / TILE), tr((G.player_y + PLAYERH / 2) / TILE)) === 42)
        if (rooms[G.player_room].enemies === 0) can_move = 0;
  if (rooms[G.player_room].room_type === 6)
    if (CanGetArtifact())
      if (
        PlayerDist(rooms[G.player_room].w * S(16) + rooms[G.player_room].x * TILE, rooms[G.player_room].h * S(16) + rooms[G.player_room].y * TILE) <
        TILE
      )
        if (rooms[G.player_room].enemies === 0) if (G.current_boss === 3) can_move = 0;
  if (G.scroll_home !== 0) can_move = 0;
  if (G.boss_fight_mode === 1) can_move = 0;
  if (G.boss_fight_mode >= 3) can_move = 0;
  if (G.opening_door_i !== 0) can_move = 0;
  if (G.game_paused) can_move = 0;

  await handleEvents();
  if (G.map_enabled) G.game_paused = 1;

  if (!G.game_paused) G.expired_ms += FRAME_MS;

  if (!G.game_paused) {
    processAbilityMovement(world, input, releaseCircuit);
  }

  if (can_move) {
    let ix = G.player_x;
    let iy = G.player_y;
    let off_x = 0;
    let off_y = 0;
    const spd = G.player_walk_speed * (G.artifacts[4] ? 1.4 : 1);
    if (keyHeld[K_UP] && !keyHeld[K_DN]) {
      iy = tr(iy - spd);
      G.player_dir = 0;
    }
    if (keyHeld[K_DN] && !keyHeld[K_UP]) {
      iy = tr(iy + spd);
      G.player_dir = 1;
      off_y = S(24);
    }
    if (keyHeld[K_LT] && !keyHeld[K_RT]) {
      ix = tr(ix - spd);
      if (!(keyHeld[K_UP] || keyHeld[K_DN])) G.player_dir = 3;
    }
    if (keyHeld[K_RT] && !keyHeld[K_LT]) {
      off_x = S(16);
      ix = tr(ix + spd);
      if (!(keyHeld[K_UP] || keyHeld[K_DN])) G.player_dir = 2;
    }

    if (ix !== G.player_x || iy !== G.player_y) {
      if (tr(G.player_x / TILE) !== tr((ix + off_x) / TILE) || tr(G.player_y / TILE) !== tr((iy + off_y) / TILE)) {
        if (touchTile(ix, iy)) {
          G.player_wlk = (G.player_wlk + 1 + G.artifacts[4] * 3) % (4 * G.wlk_wait);
        } else {
          if (touchTile(G.player_x, iy)) {
            G.player_wlk = (G.player_wlk + 1 + G.artifacts[4] * 3) % (4 * G.wlk_wait);
          } else {
            if (touchTile(ix, G.player_y)) {
              G.player_wlk = (G.player_wlk + 1 + G.artifacts[4] * 3) % (4 * G.wlk_wait);
              if (off_x > 0) G.player_dir = 2;
              else G.player_dir = 3;
            }
          }
        }
      } else {
        G.player_x = ix;
        G.player_y = iy;
        G.player_wlk = (G.player_wlk + 1 + G.artifacts[4] * 3) % (4 * G.wlk_wait);
      }
    }
  }

  if (ctx.t % (33 * 10) === 33 * 10 - 1) ActivateRand();

  if (G.voluntary_exit && G.enter_pressed) {
    G.voluntary_exit = 0;
    G.game_running = 0;
    G.game_paused = 0;
    hideGameHud();
  }

  const breakLoop = (G.player_lives === 0 && !G.training) || G.show_ending !== 0;
  return { breakLoop, showEnding: G.show_ending !== 0 };
}

export async function stepDungeonSimulation(
  world: World,
  ctx: DungeonFrameCtx,
  input: InputFrame,
  releaseCircuit: () => void,
  touchTile: (ix: number, iy: number) => number
): Promise<void> {
  applyInputFrameToKeyHeld(input);
  updateDungeonPreRender(world, ctx);
  await updateDungeonPostPresent(world, input, ctx, async () => undefined, releaseCircuit, touchTile);
}
